import { useState } from 'react';
import { motion } from 'framer-motion';

import { useParams, useSearchParams } from 'react-router-dom';
import { containerVariants } from 'Constants';
import { useSearchWithAnalyticsJobs } from 'Hooks';
import { clx, jobStatus, jobStatusWithoutDraft } from 'Utils';
import {
	DataTable,
	DataTableBody,
	DataTableHead,
	DataTableLoading,
	DataTableRowCell,
	DetailPartnerJobRow,
	ListingPage,
	NoData
} from 'Elements';
import { useTranslation } from 'react-i18next';
import { BadgeStatus } from 'src/types/Global';

export const DetailPartnerJobList = () => {
	const { partner_id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const statusParam = searchParams.get('status') || '';
	const { t } = useTranslation();
	const params = Object.fromEntries([...searchParams]);
	const pageParam = searchParams.get('page') || 1;
	const queryParam = searchParams.get('q') || null;

	const [state, setState] = useState({
		pageSize: 10,
		currentPage: pageParam,
		keyword: queryParam,
		filterBy: ''
	});

	const { data, isLoading } = useSearchWithAnalyticsJobs({
		tenantId: partner_id as string,
		keyword: state.keyword,
		jobStatus: state.filterBy
	});

	const getFilterByName = (filter_name: string) => {
		return jobStatus.find((filter) => filter.title === filter_name);
	};

	const handleFilterBy = (selected_filter: Partial<BadgeStatus>) => {
		setSearchParams({
			...params,
			page: '1',
			status: selected_filter.title?.toString() ?? ''
		});
		setState((prev) => ({
			...prev,
			filterBy: selected_filter.value?.toString() ?? '',
			currentPage: 1
		}));
	};

	const handleSearch = (query: string) => {
		setSearchParams({
			...params,
			page: '1',
			q: query
		});
		setState((prev) => ({
			...prev,
			currentPage: 1,
			keyword: query || null
		}));
	};

	const cellClassName = (col: string) => {
		const cols: { [x: string]: string } = {
			title: 'w-sm-30',
			'job-type': 'w-sm-15',
			status: 'w-sm-15',
			'published-date': 'w-sm-30',
			'total-view': 'w-sm-10'
		};
		return clx('w-100', cols[col.toLowerCase()]);
	};

	return (
		<ListingPage
			selectedPageSize={state.pageSize}
			filterOptions={jobStatusWithoutDraft}
			selectedFilter={t(
				getFilterByName(statusParam)?.label ?? 'dropdown.all'
			)}
			onFilterChange={handleFilterBy}
			onSearchChange={handleSearch}>
			<DataTable>
				{data && (
					<DataTableHead>
						<DataTableRowCell
							cellText="table.title"
							cellClassName={cellClassName('title')}
						/>
						<DataTableRowCell
							cellText="table.job-type"
							cellClassName={cellClassName('job-type')}
						/>
						<DataTableRowCell
							cellText="table.status"
							cellClassName={cellClassName('status')}
						/>
						<DataTableRowCell
							cellText="table.published-date"
							cellClassName={cellClassName('published-date')}
						/>
						<DataTableRowCell
							cellText="table.total-view"
							cellClassName={cellClassName('total-view')}
						/>
					</DataTableHead>
				)}
				<DataTableBody>
					{!isLoading && (
						<motion.div
							variants={containerVariants}
							initial="hidden"
							animate="show">
							{data?.map((partner: any) => (
								<DetailPartnerJobRow
									cellClassName={cellClassName}
									key={partner?.jobId}
									partner={partner}
								/>
							))}
						</motion.div>
					)}
					{isLoading && (
						<DataTableLoading
							widths={[30, 10, 15, 30, 15]}
							count={state.pageSize}
						/>
					)}
				</DataTableBody>
				{!isLoading && data?.length === 0 && (
					<NoData message="title.no-partner-yet" />
				)}
			</DataTable>
		</ListingPage>
	);
};
